import { useEffect } from "react";
import { Sparkles } from "lucide-react";
import { captureEvent } from "@/lib/analyticsQueue";
import { useCredits } from "@/contexts/CreditsContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { PricingContent } from "@/components/flowly/PricingContent/PricingContent";
import { ReferralBonusBanner } from "@/components/flowly/ReferralBonusBanner/ReferralBonusBanner";
import { ANALYTICS_EVENTS } from "@shared/schemas/analytics";
import { setPurchaseReturn } from "@/lib/purchaseReturn";

/**
 * Credit-wall upgrade modal. Opens from `useCredits().showPricingModal` when a
 * generation hits insufficient credits (local pre-check or server 402). Renders
 * the purchase options inline — token packs first, then the paid plans — so the
 * user can buy without leaving the flow (previously this redirected to /pricing
 * and lost almost everyone).
 */
export function PricingModal() {
  const { showPricingModal, setShowPricingModal, pricingModalContext } = useCredits();
  const { profile } = useAuth();
  const { t } = useLanguage();
  const isPaidPlan = profile?.subscription_name && profile.subscription_name !== "free";
  /** Plan-gated feature opens this modal via `openPricingModal("starter_plan_required")`
   *  so we can swap copy to "this feature needs Starter+" instead of the
   *  default "you're out of credits" wall. */
  const isStarterGate = pricingModalContext === "starter_plan_required";
  const title = isStarterGate
    ? t.pricingModal.starterRequiredTitle
    : t.pricingModal.title;
  const description = isStarterGate
    ? t.pricingModal.starterRequiredDescription
    : isPaidPlan
      ? t.pricingModal.paidDescription
      : t.pricingModal.description;

  useEffect(() => {
    if (showPricingModal) {
      captureEvent(ANALYTICS_EVENTS.PRICING_MODAL_VIEW, {
        trigger_source: window.location.pathname,
      });
      // The modal only opens from inside a flow that hit the credit wall, so
      // the current path IS the flow to return to after purchase. Remembered
      // here (survives the payment-gateway round-trip) and consumed on the
      // payment-result screen.
      setPurchaseReturn(window.location.pathname + window.location.search);
    }
  }, [showPricingModal]);

  return (
    <Dialog open={showPricingModal} onOpenChange={setShowPricingModal}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-center sm:text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-brand to-brand-secondary rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <DialogTitle className="text-3xl font-black text-foreground">
            {title}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-base">
            {description}
          </DialogDescription>
        </DialogHeader>

        <ReferralBonusBanner className="mt-2" />

        {/* Gated on open so usePricing() only runs while the modal is shown
            (Radix unmounts DialogContent when closed). */}
        <div className="mt-4">
          <PricingContent variant="modal" />
        </div>
      </DialogContent>
    </Dialog>
  );
}
